/**
 * edit.js
 * Gestión del panel de edición y creación de recetas (solo admin).
 */

import { supabase } from './supabase.js';
import { getRecetas, setRecetas } from './state.js';

const panel = document.getElementById('panel-edicion');
const tituloPanel = panel.querySelector('.edicion-panel__titulo');
const inputNombre = document.getElementById('edit-nombre');
const selectCat = document.getElementById('edit-categoria');
const textareaIng = document.getElementById('edit-ingredientes');
const textareaPrep = document.getElementById('edit-preparacion');
const errorEl = document.getElementById('edicion-error');
const btnGuardar = document.getElementById('btn-guardar-edicion');
const btnCerrar = document.getElementById('btn-cerrar-edicion');
const btnEliminar = document.getElementById('btn-eliminar-receta');

let _recetaActual = null; // null = modo creación, objeto = modo edición
let _onGuardado = null;

// ── Abrir en modo edición ─────────────────────────────

/**
 * Abre el panel con los datos de una receta existente.
 * @param {Object}   receta
 * @param {Function} onGuardado
 */
export function abrirEdicion(receta, onGuardado) {
  _recetaActual = receta;
  _onGuardado = onGuardado;
  tituloPanel.textContent = 'Editar receta';
  btnGuardar.textContent = 'Guardar cambios';
  btnEliminar.style.display = 'block';

  inputNombre.value = receta.nombre;
  selectCat.value = receta.categoria;
  textareaIng.value = receta.ingredientes;
  textareaPrep.value = receta.preparacion;
  errorEl.textContent = '';

  _mostrarPanel();
}

// ── Abrir en modo creación ────────────────────────────

/**
 * Abre el panel con los campos vacíos para crear una receta nueva.
 * @param {Function} onGuardado
 */
export function abrirCreacion(onGuardado) {
  _recetaActual = null;
  _onGuardado = onGuardado;
  tituloPanel.textContent = 'Nueva receta';
  btnGuardar.textContent = 'Crear receta';
  btnEliminar.style.display = 'none';

  inputNombre.value = '';
  selectCat.value = 'Primer Plato';
  textareaIng.value = '';
  textareaPrep.value = '';
  errorEl.textContent = '';

  _mostrarPanel();
}

// ── Cerrar panel ──────────────────────────────────────

export function cerrarEdicion() {
  panel.classList.remove('edicion-panel--visible');
  panel.setAttribute('aria-hidden', 'true');
  _recetaActual = null;
}

// ── Guardar (crea o actualiza según el modo) ──────────

async function guardarCambios() {
  const cambios = {
    nombre: inputNombre.value.trim(),
    categoria: selectCat.value,
    ingredientes: textareaIng.value.trim(),
    preparacion: textareaPrep.value.trim(),
  };

  if (!cambios.nombre) {
    errorEl.textContent = 'El nombre no puede estar vacío';
    return;
  }

  btnGuardar.disabled = true;
  errorEl.textContent = '';
  const textoOriginal = btnGuardar.textContent;
  btnGuardar.textContent = 'Guardando...';

  if (_recetaActual) {
    await _actualizar(cambios);
  } else {
    await _crear(cambios);
  }

  btnGuardar.disabled = false;
  btnGuardar.textContent = textoOriginal;
}

// ── Actualizar receta existente ───────────────────────

async function _actualizar(cambios) {
  const { error } = await supabase.from('recetas').update(cambios).eq('id', _recetaActual.id);

  if (error) {
    errorEl.textContent = 'Error al guardar: ' + error.message;
    return;
  }

  // Actualizar estado local sin recargar todo
  const actualizadas = getRecetas().map((r) =>
    r.id === _recetaActual.id ? { ...r, ...cambios } : r
  );
  setRecetas(actualizadas);

  cerrarEdicion();
  if (_onGuardado) {
    _onGuardado();
  }
}

// ── Crear receta nueva ────────────────────────────────

async function _crear(cambios) {
  // El ID lo genera Supabase automáticamente mediante secuencia
  const { data, error } = await supabase.from('recetas').insert(cambios).select().single();

  if (error) {
    errorEl.textContent = 'Error al crear: ' + error.message;
    return;
  }

  setRecetas([...getRecetas(), data]);

  cerrarEdicion();
  if (_onGuardado) {
    _onGuardado();
  }
}

// ── Helpers privados ──────────────────────────────────

function _mostrarPanel() {
  panel.classList.add('edicion-panel--visible');
  panel.setAttribute('aria-hidden', 'false');
  panel.scrollTop = 0;
  inputNombre.focus();
}

// ── Eliminar receta ───────────────────────────────────

async function eliminarReceta() {
  if (!_recetaActual) {
    return;
  }

  const confirmar = window.confirm(
    `¿Eliminar "${_recetaActual.nombre}"? Esta acción no se puede deshacer.`
  );
  if (!confirmar) {
    return;
  }

  btnEliminar.disabled = true;
  btnEliminar.textContent = 'Eliminando...';

  const { error } = await supabase.from('recetas').delete().eq('id', _recetaActual.id);

  if (error) {
    errorEl.textContent = 'Error al eliminar: ' + error.message;
    btnEliminar.disabled = false;
    btnEliminar.textContent = 'Eliminar receta';
    return;
  }

  // Eliminar del estado local
  const actualizadas = getRecetas().filter((r) => r.id !== _recetaActual.id);
  setRecetas(actualizadas);

  cerrarEdicion();
  if (_onGuardado) {
    _onGuardado();
  }
}

// ── Eventos del panel ─────────────────────────────────

btnCerrar.addEventListener('click', cerrarEdicion);
btnGuardar.addEventListener('click', guardarCambios);
btnEliminar.addEventListener('click', eliminarReceta);
panel.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') {
    cerrarEdicion();
  }
});
